import React, { useState } from 'react';
import SparkOptions from 'sharedComponents/UI/SparkOptions';
import { CreateFavouriteListFormValues } from 'components/common/Favourite/CreateFavouriteListPopUp';
import ReviewFormModal, {
  type ReviewFormModalValues,
} from 'components/common/ReviewFormModal';
import SparkReport, {
  type SparkReportFormValues,
} from 'components/common/Report/SparkReport';
import ConfirmationDialog from 'components/common/ConfirmationDialog';
import SparkShareModal from 'components/common/Share/SparkShareModal';
import FavouritePopUp from 'components/common/Favourite';
import { useDialog, usePopover } from 'hooks/common';
import type { DropdownOption } from 'types/common/DropdownOption';
import type {
  ActionOptions,
  LoggedInUserFavouriteListMeta,
  Rating,
} from 'types/common';
import { SPARK_MODAL_TYPES } from 'constants/common';
import type { LoggedInUserMeta } from 'types/common';

type SparkOptionsModalProps = {
  options: ActionOptions[] | undefined;
  resourceType: 'spark' | 'memory';
  resourceID: string | undefined;
  resourceAccessLinkToken?: string | null;
  privacyTypeId?: number | null;
  resourceLogoURL: string | undefined;
  isResourceFavourite?: boolean | undefined;
  resourceName: string | undefined;
  handleSparkRatingSave: (values: ReviewFormModalValues) => Promise<void>;
  userRating: Rating | undefined;
  allowedToReview: boolean | undefined;

  onDelete: () => void;
  onArchive: () => void;

  onCreateList?: (values: CreateFavouriteListFormValues) => void;
  onFavouriteItemClick?: (itemID: number) => void;
  isLoading?: boolean | undefined;
  existingFavouriteList?: LoggedInUserFavouriteListMeta[];

  onSendReport?: (values: SparkReportFormValues) => void;
  flagReasonsDropdownOptions?: DropdownOption[] | undefined;
  showShareIcon?: boolean;

  showDotsIcon?: boolean;
  showHeartIcon?: boolean;
  transformMenu?: boolean;

  userPermissions: LoggedInUserMeta['permissions'];
};

function SparkOptionsModal({
  options,
  userRating,
  allowedToReview,
  handleSparkRatingSave,
  resourceType,
  onDelete = () => {},
  resourceID,
  existingFavouriteList = [],
  isLoading: isFavouriteLoading,
  onCreateList = () => {},
  onFavouriteItemClick = () => {},
  flagReasonsDropdownOptions,
  onSendReport = () => {},
  resourceLogoURL = '',
  showShareIcon = true,
  showDotsIcon = true,
  showHeartIcon = true,
  isResourceFavourite,
  resourceName,
  transformMenu = false,
  userPermissions,
  onArchive,
  resourceAccessLinkToken,
  privacyTypeId,
}: SparkOptionsModalProps) {
  const [modalType, setModalType] = React.useState<
    keyof typeof SPARK_MODAL_TYPES | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const { open, closeDialog, openDialog } = useDialog();
  const {
    anchorEl,
    open: isPopoverOpen,
    openPopover,
    closePopover,
  } = usePopover();

  const onOptionClick = (
    type: keyof typeof SPARK_MODAL_TYPES | null,
    event?: React.MouseEvent<HTMLButtonElement>
  ) => {
    openDialog();
    setModalType(type);
    if (event) {
      openPopover(event);
    }
  };

  const onDialogClose = () => {
    setModalType(null);
    closeDialog();
    closePopover();
  };

  const deleteResource = async () => {
    setIsLoading(true);
    await onDelete();
    setTimeout(() => {
      setIsLoading(false);
      onDialogClose();
    }, 1500);
  };
  const archiveResource = async () => {
    setIsLoading(true);
    await onArchive();
    setTimeout(() => {
      setIsLoading(false);
      onDialogClose();
    }, 1500);
  };

  const resource = resourceType === 'spark' ? 'Spark' : 'Memory';
  return (
    <>
      <SparkOptions
        menuItems={options}
        onOptionClick={onOptionClick}
        onDialogClose={onDialogClose}
        showShareIcon={showShareIcon}
        showDotsIcon={showDotsIcon}
        showHeartIcon={showHeartIcon}
        isResourceFavourite={isResourceFavourite}
        transformMenu={transformMenu}
      />
      {modalType === SPARK_MODAL_TYPES.share && isPopoverOpen && (
        <SparkShareModal
          open={open}
          anchorEl={anchorEl}
          handleClose={onDialogClose}
          resourceType={resourceType}
          resourceID={resourceID}
          resourceAccessLinkToken={resourceAccessLinkToken}
          privacyTypeId={privacyTypeId}
        />
      )}
      {modalType === SPARK_MODAL_TYPES.favourite && isPopoverOpen && (
        <FavouritePopUp
          open={open}
          anchorEl={anchorEl}
          onCreateList={onCreateList}
          onFavouriteItemClick={onFavouriteItemClick}
          isLoading={isFavouriteLoading}
          existingFavouriteList={existingFavouriteList}
          handleClose={onDialogClose}
        />
      )}
      {modalType === SPARK_MODAL_TYPES.review && open && (
        <ReviewFormModal
          open={open}
          onClose={closeDialog}
          rating={userRating}
          allowedToReview={allowedToReview}
          onSave={handleSparkRatingSave}
          logoURL={resourceLogoURL}
          resourceName={resourceName}
        />
      )}
      {modalType === SPARK_MODAL_TYPES.report && isPopoverOpen && (
        <SparkReport
          open={open}
          anchorEl={anchorEl}
          flagReasonsDropdownOptions={flagReasonsDropdownOptions}
          handleClose={onDialogClose}
          onSendReport={onSendReport}
          reportType={resourceType === 'spark' ? 'Spark' : 'Memory'}
        />
      )}
      {modalType === SPARK_MODAL_TYPES.delete && open && (
        <ConfirmationDialog
          open={open}
          onClose={onDialogClose}
          onPrimaryButtonClick={
            userPermissions?.canDelete ? deleteResource : onDialogClose
          }
          hideSecondaryButton={false}
          primaryButtonText='Yes'
          secondaryButtonText='No'
          title={
            userPermissions?.canDelete
              ? `Delete ${resource}?`
              : 'Permission Issue'
          }
          confirmationMessage={
            userPermissions?.canDelete
              ? `Are you sure you want to permanently delete this ${resource}?`
              : `You are not allowed to delete this ${resource}`
          }
          loading={isLoading}
        />
      )}

      {modalType === SPARK_MODAL_TYPES.archive && open && (
        <ConfirmationDialog
          open={open}
          onClose={onDialogClose}
          onPrimaryButtonClick={archiveResource}
          primaryButtonText={`Yes`}
          secondaryButtonText='No'
          title={`Archive ${resource}?`}
          confirmationMessage={
            resourceType === 'memory'
              ? 'Are you sure you want to archive this Memory? This memory will stop working and all related files will be removed.'
              : `Are you sure you want to archive this ${resource}?`
          }
        />
      )}
    </>
  );
}

export default SparkOptionsModal;
